import React from 'react';
import { Table, Tooltip, Typography } from 'antd';
import { buildContinuousAvailability, DATE_SUB_COLUMNS, rebalanceHint } from '../inventoryData';
import useIsMobile from '../../../../hooks/useIsMobile';
import { FONT } from "../../../../styles/typography";

const { Text } = Typography;

const DANGER = '#ff4d4f';
// 通路失衡提醒用橘色，與「售完/為負」的紅色區隔 —— 前者是待調整，後者是已無房
const REBALANCE_FG = '#d46b08';

/**
 * 「可售」是業務最常看的數字，用粗體，售完 (0) 或為負轉紅。
 *
 * 負數多半不是真的超賣：實測 39 筆負數中，只有 3 筆 ORDER_QNT+USE_QNT > ROOM_QNT，
 * 其餘 36 筆是鎖控沒釋出造成的帳面效果 —— 例如 2025/03/08 房型 STL，
 * 總房 14、在住 14 (剛好滿房)、鎖控 2 間仍掛著，於是可售被算成 −2。
 *
 * 「現場可售 + 線上銷售」= 當日還能賣的總量，差別只在通路：現場可售是櫃台/直訂能立刻賣的，
 * 線上銷售要走 OTA 或請訂房組釋出。德安的 WRS 是所有線上通路的共同進單閘道，
 * Booking.com、易遊網、Expedia、C-TRIP、Klook 與飯店官網共用同一塊配額
 * (實測官網僅佔其中 6.7%)。
 */
function PrimaryValue({ cell, hint }) {
  if (hint) {
    return (
      <Tooltip title={hint.message}>
        <b style={{ color: REBALANCE_FG }}>{cell.left_qnt}</b>
      </Tooltip>
    );
  }

  let tip;
  if (cell.left_qnt < 0) {
    tip = `可售為 ${cell.left_qnt} —— 已訂/在住加上未釋出的鎖控已超過總房數，多半是鎖控沒放掉，不一定是真的賣超過房間數`;
  } else if (cell.wrs_left > 0) {
    tip = `現場可直接賣 ${cell.left_qnt} 間；另有 ${cell.wrs_left} 間保留給網路通路 (OTA 賣得到，現場要賣需請訂房組釋出)。當日可賣總量 ${cell.left_qnt + cell.wrs_left} 間`;
  } else {
    tip = `可直接賣 ${cell.left_qnt} 間`;
  }

  return (
    <Tooltip title={tip}>
      <b style={{ color: cell.left_qnt <= 0 ? DANGER : 'inherit' }}>{cell.left_qnt}</b>
    </Tooltip>
  );
}

/** 非主數字的子欄位。未售小計是推導值，用底色與粗體與原始欄位區隔。 */
function SubValue({ cell, sub, hint }) {
  const value = sub.compute ? sub.compute(cell) : cell[sub.key];

  if (hint) {
    return (
      <Tooltip title={hint.message}>
        <b style={{ color: REBALANCE_FG }}>{value}</b>
      </Tooltip>
    );
  }

  return <span style={sub.subtotal ? { fontWeight: 600 } : {}}>{value}</span>;
}

/**
 * 右側「連續可售」。區間內只要有日期未開放，這個數字就只採計得到資料的那幾天，
 * 必須標記出來 —— 否則會被當成整段區間都能賣這麼多。
 */
function ContinuousValue({ summary }) {
  if (summary.value === null) return <Text type="secondary">—</Text>;

  const partial = summary.missing > 0;
  const tip = partial
    ? `區間內有 ${summary.missing} 天 PMS 尚未開放，此數字只採計有資料的 ${summary.counted} 天`
    : `整段 ${summary.counted} 天都要住的話，最多能接 ${summary.value} 間 (含線上銷售，需請訂房組釋出)`;

  return (
    <Tooltip title={tip}>
      <span>
        <b style={{ color: summary.value <= 0 ? DANGER : 'inherit' }}>{summary.value}</b>
        {partial && <sup style={{ color: '#8c8c8c', marginLeft: 2 }}>部分</sup>}
      </span>
    </Tooltip>
  );
}

/**
 * 橫軸日期、縱軸房型。每個日期底下再分「可售 / 線上銷售 / 其他保留 / 超收」子欄位；
 * 下方是各日期的可售合計，右側是各房型的連續可售。
 */
export default function InventoryMatrix({ dateColumns = [], rows = [], totalCells = {} }) {
  const isMobile = useIsMobile();

  // 樣式一律用 className (定義於 App.less)：antd 的 Table.Summary.Cell 只接受 className，
  // 傳 style 不會落到 <td>，合計列會失去底色而不報錯。
  const cellClassName = (col, sub) => {
    if (sub && sub.subtotal) {
      return col.isWeekend ? 'inv-subtotal-weekend' : 'inv-subtotal';
    }
    return col.isWeekend ? 'inv-weekend' : undefined;
  };

  const cellProps = (col, sub) => {
    const className = cellClassName(col, sub);
    return className ? { className } : {};
  };

  const subColumnDef = (col, sub) => ({
    title: (
      <Tooltip title={sub.tip}>
        <span style={{ whiteSpace: 'nowrap', fontWeight: sub.primary || sub.subtotal ? 600 : 400 }}>
          {sub.label}
        </span>
      </Tooltip>
    ),
    key: `${col.date}:${sub.key}`,
    align: 'center',
    width: sub.label.length > 2 ? 76 : 60,
    onHeaderCell: () => cellProps(col, sub),
    onCell: (row) => {
      const hint = rebalanceHint(row.cells ? row.cells[col.date] : null);
      if (hint && hint.key === sub.key) {
        return { className: 'inv-rebalance' };
      }
      return cellProps(col, sub);
    },
    render: (_, row) => {
      const cell = row.cells ? row.cells[col.date] : null;
      if (!cell) return <Text type="secondary">—</Text>;
      const hint = rebalanceHint(cell);
      const flagged = hint && hint.key === sub.key ? hint : null;
      if (sub.primary) return <PrimaryValue cell={cell} hint={flagged} />;
      return <SubValue cell={cell} sub={sub} hint={flagged} />;
    },
  });

  const dateGroupDef = (col) => ({
    title: (
      <div style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
        <span style={col.isWeekend ? { color: DANGER } : {}}>
          {col.label} ({col.weekdayLabel})
        </span>
        {!col.available && (
          <>
            {' '}
            <Text type="secondary" style={{ fontSize: FONT.caption }}>未開放</Text>
          </>
        )}
      </div>
    ),
    key: col.date,
    onHeaderCell: () => cellProps(col),
    children: DATE_SUB_COLUMNS.map((sub) => subColumnDef(col, sub)),
  });

  const columns = [
    {
      title: '房型',
      key: 'roomType',
      // 手機不釘欄：三個固定欄合計 354px 比可視寬 (390px 機型約 309px) 還大，
      // 釘住會蓋滿整個畫面而一格資料都看不到，且橫向捲動也救不回來。
      fixed: isMobile ? undefined : 'left',
      width: isMobile ? 120 : 200,
      // 房型名稱與代碼排在同一行，不折行
      render: (_, row) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {row.nam}
          {!isMobile && (
            <Text type="secondary" style={{ fontSize: FONT.caption, marginLeft: 6 }}>{row.cod}</Text>
          )}
        </span>
      ),
    },
    {
      // 房數在各日期都相同，獨立成一欄，不隨日期重複
      title: '房數',
      dataIndex: 'room_qnt',
      key: 'room_qnt',
      fixed: isMobile ? undefined : 'left',
      align: 'center',
      width: 64,
    },
    ...dateColumns.map(dateGroupDef),
    {
      title: (
        <Tooltip title="整段區間都要住的話最多能接幾間 —— 取區間內每日「未售小計」的最小值 (瓶頸日)，不是加總。含線上銷售那部分，請訂房組釋出即可轉現場">
          <span style={{ whiteSpace: 'nowrap' }}>連續可售</span>
        </Tooltip>
      ),
      key: 'continuous',
      fixed: isMobile ? undefined : 'right',
      align: 'center',
      width: 90,
      render: (_, row) => <ContinuousValue summary={buildContinuousAvailability(dateColumns, row.cells)} />,
    },
  ];

  const totalRoomQnt = rows.reduce((sum, row) => sum + (row.room_qnt || 0), 0);
  const continuousTotal = buildContinuousAvailability(dateColumns, totalCells);

  return (
    <Table
      size="small"
      rowKey="key"
      bordered
      columns={columns}
      dataSource={rows}
      pagination={false}
      scroll={{ x: 'max-content' }}
      summary={() => (
        <Table.Summary fixed="bottom">
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} fixed="left">
              <Text strong>合計</Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1} fixed="left" align="center">
              <Text strong>{totalRoomQnt}</Text>
            </Table.Summary.Cell>
            {dateColumns.flatMap((col, colIndex) =>
              DATE_SUB_COLUMNS.map((sub, subIndex) => {
                const cell = totalCells[col.date];
                return (
                  <Table.Summary.Cell
                    key={`${col.date}:${sub.key}`}
                    index={2 + colIndex * DATE_SUB_COLUMNS.length + subIndex}
                    align="center"
                    // 合計列不吃 columns 的 onCell，底色要自己補
                    {...cellProps(col, sub)}
                  >
                    {cell
                      ? (
                        <span style={{ fontWeight: sub.primary || sub.subtotal ? 600 : 400 }}>
                          {sub.compute ? sub.compute(cell) : cell[sub.key]}
                        </span>
                      )
                      : <Text type="secondary">—</Text>}
                  </Table.Summary.Cell>
                );
              })
            )}
            <Table.Summary.Cell
              index={2 + dateColumns.length * DATE_SUB_COLUMNS.length}
              fixed="right"
              align="center"
            >
              <ContinuousValue summary={continuousTotal} />
            </Table.Summary.Cell>
          </Table.Summary.Row>
        </Table.Summary>
      )}
    />
  );
}
